import {
  Activity,
  BadgeDollarSign,
  Bot,
  CheckCircle2,
  ChevronRight,
  CircleDotDashed,
  CreditCard,
  ExternalLink,
  Flame,
  Gift,
  LockKeyhole,
  MessageSquare,
  MonitorCog,
  Radar,
  RadioTower,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Store,
  Swords,
  Terminal,
  UnlockKeyhole,
  Wallet,
  X,
  Zap
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { chatWithAgents, fetchConfig, runX402Smoke, spawnCase, startStorm } from "./lib/api";
import { SCAM_TEMPLATES } from "./lib/scams";
import { getInitialLocale, localizeTemplate, TEXT, type Locale } from "./i18n";
import type { ArenaCase, ArenaConfig, ChatTurn, PaymentStep, ScamTemplate, ScamType, StormResult, X402SmokeResult } from "./lib/types";

type EthereumProvider = {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
};

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

const IconMap = {
  Gift,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Radar
};

type AppView = "arena" | "spawn" | "admin" | "market";
type BusyTask = "spawn" | "storm" | "chat" | "smoke";
type AudienceCustomKind = "scam" | "safe";
const SCAM_TYPE_IDS = new Set<ScamType>(SCAM_TEMPLATES.map((item) => item.id));

type StreamEvent = {
  id: string;
  label: string;
  detail: string;
  tone: "red" | "green" | "yellow" | "blue";
  href?: string;
};
type JudgeModalState =
  | { status: "running"; template: ScamTemplate }
  | { status: "done"; template: ScamTemplate; case: ArenaCase }
  | { status: "error"; template: ScamTemplate; message: string };

function viewFromPath(pathname: string): AppView {
  if (pathname.startsWith("/spawn")) return "spawn";
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/intel-market")) return "market";
  return "arena";
}

function shortAddress(value: string | undefined, fallback: string) {
  if (!value) return fallback;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function shortHash(value?: string) {
  if (!value) return "";
  return `${value.slice(0, 10)}...${value.slice(-8)}`;
}

function eventId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function initialEvents(locale: Locale): StreamEvent[] {
  const t = TEXT[locale];
  return [
    { id: "boot-1", label: t.events.bootArena, detail: t.events.waiting, tone: "blue" },
    { id: "boot-2", label: t.events.whiteAgent, detail: t.events.rulesArmed, tone: "green" }
  ];
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(() => getInitialLocale());
  const t = TEXT[locale];
  const [view, setView] = useState<AppView>(() => viewFromPath(window.location.pathname));
  const [config, setConfig] = useState<ArenaConfig | null>(null);
  const [selected, setSelected] = useState<ScamTemplate>(SCAM_TEMPLATES[0]);
  const [budgetUSDC, setBudgetUSDC] = useState(0.005);
  const [cases, setCases] = useState<ArenaCase[]>([]);
  const [events, setEvents] = useState<StreamEvent[]>(() => initialEvents(locale));
  const [storm, setStorm] = useState<StormResult | null>(null);
  const [smoke, setSmoke] = useState<X402SmokeResult | null>(null);
  const [busy, setBusy] = useState<BusyTask | null>(null);
  const busyRef = useRef<BusyTask | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [judgeModal, setJudgeModal] = useState<JudgeModalState | null>(null);
  const [walletAddress, setWalletAddress] = useState<string>("");
  const [walletChainId, setWalletChainId] = useState<string>("");
  const [walletError, setWalletError] = useState<string | null>(null);
  const [customKind, setCustomKind] = useState<AudienceCustomKind>("scam");
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [chatInput, setChatInput] = useState<string>(t.chat.defaultQuestion);
  const [chat, setChat] = useState<ChatTurn[]>([
    {
      role: "assistant",
      content: t.chat.intro
    }
  ]);

  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
    window.localStorage.setItem("rug-rumble-locale", locale);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", locale);
    window.history.replaceState(null, "", url);
  }, [locale]);

  useEffect(() => {
    const syncView = () => setView(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", syncView);
    return () => window.removeEventListener("popstate", syncView);
  }, []);

  useEffect(() => {
    setChat((items) =>
      items.length === 1 && items[0].role === "assistant" ? [{ role: "assistant", content: t.chat.intro }] : items
    );
    setChatInput((value) =>
      value === TEXT.zh.chat.defaultQuestion || value === TEXT.en.chat.defaultQuestion ? t.chat.defaultQuestion : value
    );
  }, [locale, t.chat.defaultQuestion, t.chat.intro]);

  useEffect(() => {
    fetchConfig()
      .then(setConfig)
      .catch((err) => setError(err instanceof Error ? err.message : t.errors.config));
  }, [t.errors.config]);

  const latest = cases[0];
  const selectedTemplate = useMemo(() => localizeTemplate(selected, locale), [selected, locale]);
  const latestTemplate = useMemo(
    () => (latest ? localizeTemplate(latest.template, locale) : selectedTemplate),
    [latest, locale, selectedTemplate]
  );
  const localPreview = useMemo(() => {
    const risky = selected.groundTruth !== "safe";
    return {
      baseRisk: risky ? 80 : 12,
      confidence: risky ? 0.68 : 0.9,
      rules: selectedTemplate.riskFlags.map((flag) => `${flag}`)
    };
  }, [selected.groundTruth, selectedTemplate.riskFlags]);
  const stats = useMemo(() => {
    const total = cases.length + (storm?.size ?? 0);
    const stopped =
      cases.filter((item) => item.white.verdict !== "ALLOW").length + (storm?.blocked ?? 0) + (storm?.warned ?? 0);
    const correct = cases.filter((item) => item.judge.correct).length + (storm ? storm.size - storm.missed : 0);
    const avgRiskCases = cases.length
      ? Math.round(cases.reduce((sum, item) => sum + item.white.riskScore, 0) / cases.length)
      : 0;
    const paidCalls =
      cases.reduce((sum, item) => sum + (item.white.paymentSteps || []).filter((step) => step.status === "unlocked").length, 0) +
      (storm ? Math.round(storm.size * 1.6) : 0);
    const spend =
      cases.reduce((sum, item) => sum + (item.white.totalSpentUSDC || 0), 0) + (storm ? Number((storm.size * 0.0018).toFixed(4)) : 0);
    return {
      total,
      blocked: stopped,
      accuracy: total ? Math.round((correct / total) * 100) : 100,
      avgRisk: storm?.averageRisk ?? (avgRiskCases || 96),
      latency: storm?.averageLatencyMs ?? latest?.judge.latencyMs ?? 1180,
      paidCalls,
      spend
    };
  }, [cases, latest, storm]);

  function beginTask(task: BusyTask) {
    if (busyRef.current) return false;
    busyRef.current = task;
    setBusy(task);
    setError(null);
    return true;
  }

  function endTask(task: BusyTask) {
    if (busyRef.current !== task) return;
    busyRef.current = null;
    setBusy(null);
  }

  async function handleSpawn(
    scamType?: unknown,
    options?: { showModal?: boolean; custom?: { kind: AudienceCustomKind; prompt: string } }
  ) {
    if (!beginTask("spawn")) return;
    const requestedType =
      typeof scamType === "string" && SCAM_TYPE_IDS.has(scamType as ScamType) ? (scamType as ScamType) : selected.id;
    const template =
      options?.custom?.kind === "safe"
        ? SCAM_TEMPLATES.find((item) => item.id === "SafeSwap") || selected
        : options?.custom?.kind === "scam"
          ? SCAM_TEMPLATES.find((item) => item.id === "ApprovalDrain") || selected
          : SCAM_TEMPLATES.find((item) => item.id === requestedType) || selected;
    const templateForSpawn = localizeTemplate(template, locale);
    if (options?.showModal) setJudgeModal({ status: "running", template });
    pushEvent(t.events.caseSpawned, templateForSpawn.label, "yellow");
    try {
      const result = await spawnCase(requestedType, locale, budgetUSDC, options?.custom);
      setCases((items) => [result, ...items].slice(0, 12));
      if (options?.showModal) setJudgeModal({ status: "done", template: result.template, case: result });
      const template = localizeTemplate(result.template, locale);
      pushEvent(t.events.caseOpened, `#${result.caseId ?? "?"} ${template.shortLabel}`, "red", result.explorerUrl);
      for (const step of result.white.paymentSteps || []) {
        if (step.status === "unlocked") {
          pushEvent(t.events.paymentEvidence, `${step.serviceId} · ${step.priceUSDC} USDC`, "yellow", result.explorerUrl);
        } else if (step.status === "failed") {
          pushEvent(t.events.arenaError, `${step.serviceId}: ${step.error || t.x402.failed}`, "red");
        }
      }
      pushEvent(
        t.events.agentVerdict,
        `${result.white.verdict} · ${t.agents.risk} ${result.white.riskScore}`,
        "green",
        result.explorerUrl
      );
      pushEvent(t.events.reputationUpdated, `${t.agents.whiteTitle} +${result.judge.scoreDelta}`, "blue", result.explorerUrl);
    } catch (err) {
      const message = err instanceof Error ? err.message : t.errors.spawn;
      setError(message);
      if (options?.showModal) setJudgeModal({ status: "error", template, message });
      pushEvent(t.events.arenaError, t.events.spawnFailed, "red");
    } finally {
      endTask("spawn");
    }
  }

  async function handleStorm(size = 5) {
    if (!beginTask("storm")) return;
    pushEvent(t.events.rugStorm, size === 5 ? t.events.stormSpawning : `${size} cases`, "yellow");
    try {
      const result = await startStorm(size, locale);
      setStorm(result);
      pushEvent(t.events.batchSettled, `${result.blocked}/${result.size} ${t.events.blocked}`, "green", result.explorerUrl);
      pushEvent(t.events.monadEventStream, shortHash(result.txHash), "blue", result.explorerUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errors.storm);
      pushEvent(t.events.arenaError, t.events.stormFailed, "red");
    } finally {
      endTask("storm");
    }
  }

  async function handleSmoke() {
    if (!beginTask("smoke")) return;
    pushEvent(t.x402.required, X402_SERVICES_LABEL(), "yellow");
    try {
      const result = await runX402Smoke(locale);
      setSmoke(result);
      if (result.ok) {
        pushEvent(t.x402.unlocked, result.endpoint, "green");
      } else {
        setError(result.error || t.x402.smokeFailed);
        pushEvent(t.events.arenaError, result.error || t.x402.smokeFailed, "red");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t.x402.smokeFailed;
      setError(message);
      pushEvent(t.events.arenaError, message, "red");
    } finally {
      endTask("smoke");
    }
  }

  async function connectWallet() {
    setWalletError(null);
    try {
      if (!window.ethereum) throw new Error(t.wallet.noProvider);
      const accounts = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
      const chainId = (await window.ethereum.request({ method: "eth_chainId" })) as string;
      setWalletAddress(accounts[0] || "");
      setWalletChainId(chainId || "");
    } catch (err) {
      setWalletError(err instanceof Error ? err.message : t.errors.wallet);
    }
  }

  async function switchMonad() {
    setWalletError(null);
    try {
      if (!window.ethereum) throw new Error(t.wallet.noProvider);
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: "0x279f" }]
      });
      setWalletChainId("0x279f");
    } catch (err) {
      const error = err as { code?: number; message?: string };
      if (error.code === 4902 && window.ethereum) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: "0x279f",
              chainName: "Monad Testnet",
              nativeCurrency: { name: "MON", symbol: "MON", decimals: 18 },
              rpcUrls: ["https://testnet-rpc.monad.xyz"],
              blockExplorerUrls: ["https://monad-testnet.socialscan.io"]
            }
          ]
        });
        setWalletChainId("0x279f");
        return;
      }
      setWalletError(error.message || t.errors.wallet);
    }
  }

  function navigate(next: AppView) {
    setError(null);
    const path = next === "arena" ? "/arena" : next === "market" ? "/intel-market" : `/${next}`;
    if (view === next && window.location.pathname === path) return;
    const url = new URL(window.location.href);
    url.pathname = path;
    url.searchParams.set("lang", locale);
    window.history.pushState(null, "", url);
    setView(next);
  }

  function X402_SERVICES_LABEL() {
    return config?.x402?.services?.[0]?.endpoint || "/api/x402/intel/spender-reputation";
  }

  async function handleChatSubmit(event: FormEvent) {
    event.preventDefault();
    const question = chatInput.trim();
    if (!question) return;
    if (!beginTask("chat")) return;
    setChat((items) => [...items, { role: "user", content: question }]);
    setChatInput("");
    try {
      const response = await chatWithAgents(question, chat, latest?.id, locale);
      setChat((items) => [...items, { role: "assistant", content: response.answer }]);
      pushEvent(t.events.chatStep, response.source === "step-api" ? t.events.chatLive : t.events.chatFallback, "blue");
    } catch (err) {
      const message = err instanceof Error ? err.message : t.errors.chat;
      setError(message);
      setChat((items) => [...items, { role: "assistant", content: message }]);
    } finally {
      endTask("chat");
    }
  }

  function pushEvent(label: string, detail: string, tone: StreamEvent["tone"], href?: string) {
    setEvents((items) => [{ id: eventId(), label, detail, tone, href }, ...items].slice(0, 28));
  }

  return (
    <main className="app-shell">
      <section className="hero-band">
        <div className="brand-lockup">
          <img src="/brand/rug-rumble-mark.svg" alt="Rug Rumble" />
          <div>
            <p className="eyebrow">{t.eyebrow}</p>
            <h1>Rug Rumble</h1>
            <p className="subline">{t.tagline}</p>
          </div>
        </div>
        <div className="hero-actions">
          <WalletTopbar
            t={t}
            walletAddress={walletAddress}
            walletChainId={walletChainId}
            walletError={walletError}
            onConnect={connectWallet}
            onSwitch={switchMonad}
          />
          <button
            className="locale-toggle"
            type="button"
            onClick={() => setLocale((value) => (value === "zh" ? "en" : "zh"))}
          >
            {t.language}
          </button>
        </div>
        <div className="network-strip" data-testid="network-strip">
          <StatusPill
            label={t.status.contract}
            ok={Boolean(config?.ready?.contract)}
            value={shortAddress(config?.arenaAddress, t.status.notSet)}
          />
          <StatusPill
            label={t.status.rpc}
            ok={Boolean(config?.ready?.rpc)}
            value={config?.blockNumber ? `${t.status.block} ${config.blockNumber}` : t.status.checking}
          />
          <StatusPill label={t.status.step} ok={Boolean(config?.ready?.stepApi)} value={config?.model ?? "step-3.6"} />
          <StatusPill
            label={t.status.x402}
            ok={Boolean(config?.ready?.x402)}
            value={config?.x402?.network ?? "eip155:10143"}
          />
        </div>
        <nav className="view-tabs" aria-label="Rug Rumble views">
          {(["arena", "spawn", "admin", "market"] as AppView[]).map((item) => (
            <button key={item} className={view === item ? "active" : ""} type="button" onClick={() => navigate(item)}>
              {item === "arena" ? <RadioTower size={15} /> : item === "spawn" ? <Flame size={15} /> : item === "admin" ? <MonitorCog size={15} /> : <Store size={15} />}
              {t.nav[item]}
            </button>
          ))}
        </nav>
      </section>

      {error ? (
        <div className="error-banner" role="alert">
          <ShieldAlert size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      {view === "arena" ? (
        <>
      <section className="arena-grid">
        <aside className="left-rail">
          <SectionHeader icon={<Flame size={18} />} title={t.sections.spawn} />
          <div className="template-list" data-testid="scam-template-list">
            {SCAM_TEMPLATES.map((template) => {
              const Icon = IconMap[template.icon as keyof typeof IconMap] ?? ShieldAlert;
              const copy = localizeTemplate(template, locale);
              return (
                <button
                  className={`template-card ${selected.id === template.id ? "active" : ""}`}
                  data-testid={`template-${template.id}`}
                  key={template.id}
                  onClick={() => setSelected(template)}
                  style={{ "--accent": template.color } as React.CSSProperties}
                >
                  <Icon size={20} />
                  <span>
                    <strong>{copy.shortLabel}</strong>
                    <em>{template.groundTruth === "safe" ? t.chips.safe : t.chips.risk}</em>
                    <small>{copy.hiddenPayload}</small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              );
            })}
          </div>

          <div className="action-stack">
            <button className="primary-action" data-testid="spawn-case" disabled={busy !== null} onClick={() => handleSpawn()}>
              {busy === "spawn" ? <CircleDotDashed className="spin" size={18} /> : <Swords size={18} />}
              {t.buttons.judge}
            </button>
            <button className="storm-action" data-testid="start-storm" disabled={busy !== null} onClick={() => handleStorm(5)}>
              {busy === "storm" ? <CircleDotDashed className="spin" size={18} /> : <Zap size={18} />}
              {t.buttons.storm}
            </button>
          </div>
        </aside>

        <section className="battle-stage">
          <div className="stage-topline">
            <SectionHeader icon={<Activity size={18} />} title={t.sections.liveBattle} />
            <span className="case-chip">
              {latest?.caseId ? `${t.chips.caseNo} #${latest.caseId}` : t.chips.casePending}
            </span>
          </div>

          <div className="versus-layout">
            <AgentPanel
              side="black"
              title={t.agents.blackTitle}
              label={t.agents.blackLabel}
              icon={<Bot size={20} />}
              headline={latest?.black.title ?? selectedTemplate.displayedIntent}
              body={latest?.black.copy ?? selectedTemplate.hiddenPayload}
              metricLabel={t.agents.disguise}
              metricValue={`${latest?.black.disguiseScore ?? 87}/100`}
              source={latest?.black.source ?? "step-api"}
            />
            <div className="case-core">
              <div className="radar-ring">
                <Radar size={44} />
              </div>
              <h2>{latest?.black.title ?? selectedTemplate.label}</h2>
              <dl>
                <div>
                  <dt>{t.caseFields.displayedIntent}</dt>
                  <dd>{latestTemplate.displayedIntent}</dd>
                </div>
                <div>
                  <dt>{t.caseFields.actualAction}</dt>
                  <dd>{latestTemplate.calldataAction}</dd>
                </div>
                <div>
                  <dt>{t.caseFields.hiddenPayload}</dt>
                  <dd>{latestTemplate.hiddenPayload}</dd>
                </div>
              </dl>
            </div>
            <AgentPanel
              side="white"
              title={t.agents.whiteTitle}
              label={t.agents.whiteLabel}
              icon={<ShieldCheck size={20} />}
              headline={latest?.white.verdict ?? "BLOCK"}
              body={latest?.white.reason ?? selectedTemplate.hiddenPayload}
              metricLabel={t.agents.risk}
              metricValue={`${latest?.white.riskScore ?? 96}/100`}
              source={latest?.white.source ?? "step-api"}
            />
          </div>

          <WhiteBrain
            t={t}
            wallet={config?.wallets?.whiteAgent}
            budget={latest?.white.budgetUSDC ?? budgetUSDC}
            baseRisk={latest?.white.baseRisk ?? localPreview.baseRisk}
            confidence={latest?.white.confidence ?? localPreview.confidence}
            spent={latest?.white.totalSpentUSDC ?? 0}
            remaining={latest?.white.remainingBudgetUSDC ?? budgetUSDC}
            rules={latest?.white.localRules ?? localPreview.rules}
            paidIntel={latest?.white.paidIntelUsed ?? []}
          />

          <div className="metrics-row">
            <MetricCard label={t.metrics.solved} value={String(stats.total)} icon={<Terminal size={17} />} />
            <MetricCard label={t.metrics.stopped} value={String(stats.blocked)} icon={<ShieldCheck size={17} />} />
            <MetricCard label={t.metrics.accuracy} value={`${stats.accuracy}%`} icon={<Radar size={17} />} />
            <MetricCard label={t.metrics.latency} value={`${stats.latency}ms`} icon={<RadioTower size={17} />} />
            <MetricCard label={t.metrics.paidCalls} value={String(stats.paidCalls)} icon={<CreditCard size={17} />} />
            <MetricCard label={t.metrics.spend} value={`${stats.spend.toFixed(3)} USDC`} icon={<BadgeDollarSign size={17} />} />
          </div>
        </section>

        <aside className="right-rail">
          <SectionHeader icon={<CreditCard size={18} />} title={t.sections.x402Stream} />
          <X402PaymentStream steps={latest?.white.paymentSteps || smoke?.steps || []} t={t} />

          <div className="stream-divider" />
          <SectionHeader icon={<RadioTower size={18} />} title={t.sections.monadStream} />
          <div className="event-stream" data-testid="event-stream">
            {events.map((item) => (
              <a className={`stream-line ${item.tone}`} key={item.id} href={item.href} target="_blank" rel="noreferrer">
                <span className="stream-dot" />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </span>
                {item.href ? <ExternalLink size={14} /> : null}
              </a>
            ))}
          </div>

          <div className="leaderboard">
            <SectionHeader icon={<ShieldCheck size={18} />} title={t.sections.reputation} />
            <div className="leader-row">
              <span>{t.leaderboard.whiteAgent}</span>
              <strong>{stats.accuracy}%</strong>
            </div>
            <div className="leader-row">
              <span>{t.leaderboard.stopped}</span>
              <strong>{stats.blocked}</strong>
            </div>
            <div className="leader-row">
              <span>{t.leaderboard.wallet}</span>
              <strong>{shortAddress(config?.wallets.whiteAgent, t.status.notSet)}</strong>
            </div>
          </div>
        </aside>
      </section>

      <section className="lower-grid">
        <div className="chat-panel">
          <SectionHeader icon={<MessageSquare size={18} />} title={t.sections.dialogue} />
          <div className="chat-log" data-testid="chat-log">
            {chat.map((turn, index) => (
              <div className={`chat-bubble ${turn.role}`} key={`${turn.role}-${index}`}>
                {turn.content}
              </div>
            ))}
          </div>
          <form onSubmit={handleChatSubmit} className="chat-form">
            <input
              data-testid="agent-chat-input"
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              placeholder={t.chat.placeholder}
            />
            <button data-testid="agent-chat-send" disabled={busy !== null} type="submit">
              <MessageSquare size={17} />
              {t.buttons.ask}
            </button>
          </form>
        </div>

        <div className="proof-panel">
          <SectionHeader icon={<ExternalLink size={18} />} title={t.sections.proof} />
          <div className="proof-grid">
            <ProofItem
              label={t.proof.arena}
              value={shortAddress(config?.arenaAddress, t.status.notSet)}
              href={config?.arenaAddress ? `${config.explorerUrl}/address/${config.arenaAddress}` : undefined}
            />
            <ProofItem label={t.proof.latest} value={shortHash(latest?.chainTxHash) || t.status.checking} href={latest?.explorerUrl} />
            <ProofItem label={t.proof.storm} value={shortHash(storm?.txHash) || t.status.checking} href={storm?.explorerUrl} />
            <ProofItem label={t.proof.relayer} value={shortAddress(config?.wallets?.relayer, t.status.notSet)} />
          </div>
        </div>

        <WalletPanel
          t={t}
          walletAddress={walletAddress}
          walletChainId={walletChainId}
          walletError={walletError}
          onConnect={connectWallet}
          onSwitch={switchMonad}
        />
      </section>
        </>
      ) : view === "spawn" ? (
        <SpawnView
          t={t}
          locale={locale}
          selected={selected}
          budgetUSDC={budgetUSDC}
          customKind={customKind}
          customPrompt={customPrompt}
          busy={busy}
          onSelect={setSelected}
          onBudget={setBudgetUSDC}
          onCustomKind={setCustomKind}
          onCustomPrompt={setCustomPrompt}
          onSpawn={() => handleSpawn(undefined, { showModal: true })}
          onCustomSpawn={() => handleSpawn(undefined, { showModal: true, custom: { kind: customKind, prompt: customPrompt } })}
          onArena={() => navigate("arena")}
        />
      ) : view === "admin" ? (
        <AdminView t={t} smoke={smoke} busy={busy} onSmoke={handleSmoke} onPreset={async (type) => {
          setSelected(SCAM_TEMPLATES.find((item) => item.id === type) || SCAM_TEMPLATES[0]);
          await handleSpawn(type);
        }} onStorm={handleStorm} />
      ) : (
        <MarketView t={t} config={config} />
      )}

      <AudienceJudgeModal
        t={t}
        locale={locale}
        state={judgeModal}
        onClose={() => setJudgeModal(null)}
        onArena={() => {
          setJudgeModal(null);
          navigate("arena");
        }}
      />
    </main>
  );
}

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="section-header">
      {icon}
      <span>{title}</span>
    </div>
  );
}

function StatusPill({ label, ok, value }: { label: string; ok: boolean; value: string }) {
  return (
    <div className={`status-pill ${ok ? "ok" : "warn"}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

type Copy = (typeof TEXT)[Locale];

function WhiteBrain({
  t,
  wallet,
  budget,
  baseRisk,
  confidence,
  spent,
  remaining,
  rules,
  paidIntel
}: {
  t: Copy;
  wallet?: string;
  budget: number;
  baseRisk: number;
  confidence: number;
  spent: number;
  remaining: number;
  rules: string[];
  paidIntel: string[];
}) {
  const budgetPercent = Math.max(0, Math.min(100, (remaining / Math.max(0.001, budget)) * 100));
  return (
    <section className="white-brain" data-testid="white-agent-brain">
      <div>
        <SectionHeader icon={<ShieldCheck size={18} />} title={t.sections.whiteBrain} />
        <p>{t.x402.headline}</p>
      </div>
      <div className="brain-budget">
        <span>{t.x402.budget}: {budget.toFixed(3)} USDC</span>
        <strong>{t.x402.remaining}: {remaining.toFixed(3)} USDC</strong>
        <div className="budget-bar">
          <i style={{ width: `${budgetPercent}%` }} />
        </div>
      </div>
      <div className="brain-grid">
        <div><span>{t.leaderboard.wallet}</span><strong>{shortAddress(wallet, t.status.notSet)}</strong></div>
        <div><span>{t.x402.baseRisk}</span><strong>{baseRisk}/100</strong></div>
        <div><span>{t.x402.confidence}</span><strong>{Math.round(confidence * 100)}%</strong></div>
        <div><span>{t.x402.spent}</span><strong>{spent.toFixed(3)} USDC</strong></div>
      </div>
      <div className="rule-list">
        {(rules.length ? rules : [t.x402.noPayment]).slice(0, 3).map((rule) => <span key={rule}>{rule}</span>)}
      </div>
      <div className="intel-used">
        {(paidIntel.length ? paidIntel : [t.x402.noPayment]).map((item) => <strong key={item}>{item}</strong>)}
      </div>
    </section>
  );
}

function X402PaymentStream({ steps, t }: { steps: PaymentStep[]; t: Copy }) {
  const fallback = steps.length
    ? steps
    : [
        {
          serviceId: "spender-reputation",
          endpoint: "/api/x402/intel/spender-reputation",
          priceUSDC: 0.001,
          status: "planned" as const,
          network: "eip155:10143",
          createdAt: new Date().toISOString()
        }
      ];
  return (
    <div className="x402-stream" data-testid="x402-stream">
      {fallback.map((step) => (
        <article className={`payment-packet ${step.status}`} key={`${step.serviceId}-${step.createdAt}`}>
          <div className="packet-top">
            <CreditCard size={17} />
            <strong>{step.serviceId}</strong>
            <span>{step.priceUSDC.toFixed(3)} USDC</span>
          </div>
          <code>{step.endpoint}</code>
          <div className="packet-steps">
            <span className="done">{t.x402.request}</span>
            <span className={step.status === "planned" ? "" : "done"}>{t.x402.required}</span>
            <span className={step.status === "unlocked" ? "done" : step.status === "failed" ? "failed" : ""}>{step.status === "failed" ? t.x402.failed : t.x402.signing}</span>
            <span className={step.status === "unlocked" ? "done" : ""}>{t.x402.unlocked}</span>
          </div>
          <small>{step.intelSummary || step.error || `${step.network} · ${shortAddress(step.payer, "Agent wallet")}`}</small>
          {step.paymentRef ? <em>{shortHash(step.paymentRef)}</em> : null}
        </article>
      ))}
    </div>
  );
}

function WalletTopbar({
  t,
  walletAddress,
  walletChainId,
  walletError,
  onConnect,
  onSwitch
}: {
  t: Copy;
  walletAddress: string;
  walletChainId: string;
  walletError: string | null;
  onConnect: () => void;
  onSwitch: () => void;
}) {
  const connected = Boolean(walletAddress);
  const onMonad = walletChainId === "0x279f";
  return (
    <div className={`wallet-topbar ${connected ? "connected" : ""}`} data-testid="wallet-topbar">
      <button data-testid="connect-wallet" type="button" onClick={() => onConnect()} title={t.buttons.connectWallet}>
        <Wallet size={17} />
        <span>{connected ? shortAddress(walletAddress, "") : t.buttons.connectWallet}</span>
      </button>
      <button type="button" onClick={() => onSwitch()} title={t.buttons.switchMonad}>
        <UnlockKeyhole size={17} />
        <span>{onMonad ? "Monad" : t.buttons.switchMonad}</span>
      </button>
      {walletError ? <small>{walletError}</small> : null}
    </div>
  );
}

function WalletPanel({
  t,
  walletAddress,
  walletChainId,
  walletError,
  onConnect,
  onSwitch
}: {
  t: Copy;
  walletAddress: string;
  walletChainId: string;
  walletError: string | null;
  onConnect: () => void;
  onSwitch: () => void;
}) {
  return (
    <div className="wallet-panel">
      <SectionHeader icon={<Wallet size={18} />} title={t.sections.wallet} />
      <p>{t.wallet.guest}</p>
      <div className="wallet-grid">
        <div><span>{t.wallet.address}</span><strong>{walletAddress ? shortAddress(walletAddress, "") : t.wallet.disconnected}</strong></div>
        <div><span>{t.wallet.chain}</span><strong>{walletChainId || t.status.notSet}</strong></div>
      </div>
      <div className="wallet-actions">
        <button data-testid="connect-wallet-panel" type="button" onClick={() => onConnect()}><Wallet size={17} />{t.buttons.connectWallet}</button>
        <button data-testid="switch-monad-panel" type="button" onClick={() => onSwitch()}><UnlockKeyhole size={17} />{t.buttons.switchMonad}</button>
      </div>
      {walletError ? <small className="wallet-error">{walletError}</small> : null}
    </div>
  );
}

function SpawnView({
  t,
  locale,
  selected,
  budgetUSDC,
  customKind,
  customPrompt,
  busy,
  onSelect,
  onBudget,
  onCustomKind,
  onCustomPrompt,
  onSpawn,
  onCustomSpawn,
  onArena
}: {
  t: Copy;
  locale: Locale;
  selected: ScamTemplate;
  budgetUSDC: number;
  customKind: AudienceCustomKind;
  customPrompt: string;
  busy: string | null;
  onSelect: (template: ScamTemplate) => void;
  onBudget: (value: number) => void;
  onCustomKind: (value: AudienceCustomKind) => void;
  onCustomPrompt: (value: string) => void;
  onSpawn: () => void;
  onCustomSpawn: () => void;
  onArena: () => void;
}) {
  return (
    <section className="page-panel spawn-view">
      <SectionHeader icon={<Flame size={18} />} title={t.nav.spawn} />
      <h2>{t.spawnView.subtitle}</h2>
      <div className="custom-generator" data-testid="custom-case-generator">
        <div>
          <strong>{t.spawnView.customTitle}</strong>
          <small>{t.spawnView.customSubtitle}</small>
        </div>
        <div className="custom-mode">
          <button className={customKind === "scam" ? "active" : ""} disabled={busy !== null} onClick={() => onCustomKind("scam")}>
            <ShieldAlert size={16} />
            {t.spawnView.customScam}
          </button>
          <button className={customKind === "safe" ? "active" : ""} disabled={busy !== null} onClick={() => onCustomKind("safe")}>
            <ShieldCheck size={16} />
            {t.spawnView.customSafe}
          </button>
        </div>
        <textarea
          data-testid="custom-case-prompt"
          disabled={busy !== null}
          maxLength={360}
          value={customPrompt}
          onChange={(event) => onCustomPrompt(event.target.value)}
          placeholder={customKind === "safe" ? t.spawnView.customPlaceholderSafe : t.spawnView.customPlaceholderScam}
        />
        <div className="custom-suggestions">
          {(customKind === "safe" ? t.spawnView.safeIdeas : t.spawnView.scamIdeas).map((idea) => (
            <button key={idea} type="button" disabled={busy !== null} onClick={() => onCustomPrompt(idea)}>
              {idea}
            </button>
          ))}
        </div>
        <button className="primary-action" data-testid="custom-spawn-case" disabled={busy !== null} onClick={() => onCustomSpawn()}>
          {busy === "spawn" ? <CircleDotDashed className="spin" size={18} /> : <Sparkles size={18} />}
          {busy === "spawn" ? t.judgeModal.runningButton : t.spawnView.customGenerate}
        </button>
      </div>

      <h3 className="preset-title">{t.spawnView.presetTitle}</h3>
      <div className="spawn-cards">
        {SCAM_TEMPLATES.map((template) => {
          const copy = localizeTemplate(template, locale);
          return (
            <button key={template.id} className={selected.id === template.id ? "active" : ""} disabled={busy !== null} onClick={() => onSelect(template)}>
              <strong>{copy.label}</strong>
              <em>{template.groundTruth === "safe" ? t.chips.safe : t.chips.risk}</em>
              <small>{copy.hiddenPayload}</small>
            </button>
          );
        })}
      </div>
      <div className="budget-options">
        <span>{t.spawnView.budget}</span>
        <button className={budgetUSDC === 0.001 ? "active" : ""} disabled={busy !== null} onClick={() => onBudget(0.001)}>{t.spawnView.starve}</button>
        <button className={budgetUSDC === 0.005 ? "active" : ""} disabled={busy !== null} onClick={() => onBudget(0.005)}>{t.spawnView.normal}</button>
        <button className={budgetUSDC === 0.01 ? "active" : ""} disabled={busy !== null} onClick={() => onBudget(0.01)}>{t.spawnView.fund}</button>
      </div>
      <div className="action-stack horizontal">
        <button className="primary-action" disabled={busy !== null} onClick={() => onSpawn()}>
          {busy === "spawn" ? <CircleDotDashed className="spin" size={18} /> : <Swords size={18} />}
          {busy === "spawn" ? t.judgeModal.runningButton : t.buttons.judge}
        </button>
        <button className="storm-action" disabled={busy !== null} onClick={() => onArena()}><RadioTower size={18} />{t.buttons.watchArena}</button>
      </div>
    </section>
  );
}

function AudienceJudgeModal({
  t,
  locale,
  state,
  onClose,
  onArena
}: {
  t: Copy;
  locale: Locale;
  state: JudgeModalState | null;
  onClose: () => void;
  onArena: () => void;
}) {
  if (!state) return null;
  const template = localizeTemplate(state.template, locale);
  const isDone = state.status === "done";
  const verdict = isDone ? state.case.white.verdict : undefined;
  const paidIntel = isDone ? (state.case.white.paymentSteps || []).filter((step) => step.status === "unlocked") : [];
  const confidence = isDone ? Math.round((state.case.white.confidence ?? 0) * 100) : 0;
  const tone = state.status === "error" ? "error" : verdict === "ALLOW" ? "safe" : "danger";

  return (
    <div className="judge-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="judge-modal-title" data-testid="judge-modal">
      <section className={`judge-modal ${tone}`}>
        <button className="modal-close" type="button" onClick={onClose} aria-label={t.judgeModal.close}>
          <X size={18} />
        </button>
        <div className="judge-modal-head">
          <div className="judge-modal-icon">
            {state.status === "running" ? <CircleDotDashed className="spin" size={30} /> : tone === "safe" ? <ShieldCheck size={30} /> : <ShieldAlert size={30} />}
          </div>
          <div>
            <span>{template.label}</span>
            <h2 id="judge-modal-title">
              {state.status === "running" ? t.judgeModal.runningTitle : state.status === "error" ? t.judgeModal.errorTitle : t.judgeModal.doneTitle}
            </h2>
          </div>
        </div>

        {state.status === "running" ? (
          <>
            <p className="judge-modal-copy">{t.judgeModal.runningBody}</p>
            <div className="judge-modal-steps">
              <span>{t.judgeModal.stepAgent}</span>
              <span>{t.judgeModal.x402Budget}</span>
              <span>{t.judgeModal.monadProof}</span>
            </div>
          </>
        ) : state.status === "error" ? (
          <p className="judge-modal-copy">{state.message}</p>
        ) : (
          <>
            <div className="verdict-row">
              <strong className={`verdict-badge ${state.case.white.verdict.toLowerCase()}`}>{state.case.white.verdict}</strong>
              <span>{state.case.judge.correct ? t.judgeModal.correct : t.judgeModal.review}</span>
            </div>
            <div className="judge-modal-metrics">
              <div><span>{t.agents.risk}</span><strong>{state.case.white.riskScore}/100</strong></div>
              <div><span>{t.x402.confidence}</span><strong>{confidence}%</strong></div>
              <div><span>{t.x402.spent}</span><strong>{(state.case.white.totalSpentUSDC ?? 0).toFixed(3)} USDC</strong></div>
            </div>
            <p className="judge-modal-copy">{state.case.white.reason}</p>
            <div className="judge-modal-intel">
              <span>{t.judgeModal.intel}</span>
              {paidIntel.length ? paidIntel.map((step) => <strong key={`${step.serviceId}-${step.createdAt}`}>{step.serviceId}</strong>) : <em>{t.x402.noPayment}</em>}
            </div>
            <a className="proof-link" href={state.case.explorerUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={15} />
              {state.case.chainTxHash ? `${t.judgeModal.chainTx} ${shortHash(state.case.chainTxHash)}` : t.judgeModal.chainPending}
            </a>
          </>
        )}

        <div className="judge-modal-actions">
          <button data-testid="judge-modal-arena" type="button" onClick={onArena}>{t.buttons.watchArena}</button>
          <button data-testid="judge-modal-close" type="button" onClick={onClose}>{t.judgeModal.close}</button>
        </div>
      </section>
    </div>
  );
}

function AdminView({
  t,
  smoke,
  busy,
  onSmoke,
  onPreset,
  onStorm
}: {
  t: Copy;
  smoke: X402SmokeResult | null;
  busy: string | null;
  onSmoke: () => void;
  onPreset: (type: ScamType) => void;
  onStorm: (size?: number) => void;
}) {
  return (
    <section className="page-panel admin-view">
      <SectionHeader icon={<MonitorCog size={18} />} title={t.sections.admin} />
      <h2>{t.admin.subtitle}</h2>
      <div className="admin-grid">
        <button onClick={() => onSmoke()} disabled={busy !== null}>{busy === "smoke" ? <CircleDotDashed className="spin" size={17} /> : <LockKeyhole size={17} />}{t.buttons.smoke}</button>
        <button disabled={busy !== null} onClick={() => onPreset("FakeAirdrop")}>{t.admin.presetAirdrop}</button>
        <button disabled={busy !== null} onClick={() => onPreset("ApprovalDrain")}>{t.admin.presetApproval}</button>
        <button disabled={busy !== null} onClick={() => onPreset("Honeypot")}>{t.admin.presetHoneypot}</button>
        <button disabled={busy !== null} onClick={() => onStorm(5)}>{t.admin.storm5}</button>
      </div>
      <X402PaymentStream steps={smoke?.steps || []} t={t} />
      <div className={`smoke-result ${smoke?.ok ? "ok" : ""}`}>
        <strong>{smoke ? (smoke.ok ? t.x402.smokeOk : t.x402.smokeFailed) : t.x402.smokeReady}</strong>
        <small>{smoke?.error || t.admin.demoMode}</small>
      </div>
    </section>
  );
}

function MarketView({ t, config }: { t: Copy; config: ArenaConfig | null }) {
  const services = config?.x402?.services || [];
  return (
    <section className="page-panel market-view">
      <SectionHeader icon={<Store size={18} />} title={t.sections.market} />
      <h2>{t.market.subtitle}</h2>
      <div className="market-grid">
        {services.map((service) => (
          <article key={service.serviceId}>
            <div>
              <strong>{service.title}</strong>
              <span>{service.status === "live" ? t.market.live : service.status === "demo" ? t.market.demo : t.market.planned}</span>
            </div>
            <p>{service.description}</p>
            <code>{service.endpoint}</code>
            <em>{service.priceUSDC.toFixed(4)} USDC / call</em>
          </article>
        ))}
      </div>
      <p className="market-future">{t.market.future}</p>
    </section>
  );
}

function AgentPanel({
  side,
  title,
  label,
  icon,
  headline,
  body,
  metricLabel,
  metricValue,
  source
}: {
  side: "black" | "white";
  title: string;
  label: string;
  icon: React.ReactNode;
  headline: string;
  body: string;
  metricLabel: string;
  metricValue: string;
  source: string;
}) {
  return (
    <article className={`agent-panel ${side}`}>
      <div className="agent-title">
        {icon}
        <span>
          <strong>{title}</strong>
          <small>{label}</small>
        </span>
      </div>
      <h3>{headline}</h3>
      <p>{body}</p>
      <div className="agent-meter">
        <span>{metricLabel}</span>
        <strong>{metricValue}</strong>
      </div>
      <span className="source-chip">{source}</span>
    </article>
  );
}

function MetricCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="metric-card">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ProofItem({ label, value, href }: { label: string; value?: string; href?: string }) {
  const content = (
    <>
      <span>{label}</span>
      <strong>{value || "pending"}</strong>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="proof-item">
        {content}
        <ExternalLink size={15} />
      </a>
    );
  }
  return <div className="proof-item">{content}</div>;
}
